import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, refreshSession, setAccessToken, setLogoutHandler } from "../../lib/api";
import Login from "./Login";
import Layout from "./Layout";
import Dashboard from "./Dashboard";
import MenuAdmin from "./MenuAdmin";
import { PagesAdmin, NewsAdmin } from "./ContentAdmin";
import VideosAdmin from "./VideosAdmin";
import GalleryAdmin from "./GalleryAdmin";
import ProfileAdmin from "./ProfileAdmin";
import MessagesAdmin from "./MessagesAdmin";
import Settings from "./Settings";
import CalendarAdmin from "./CalendarAdmin";
import AnalyticsAdmin from "./AnalyticsAdmin";
import CalismalarimAdmin from "./CalismalarimAdmin";
import ContactsAdmin from "./ContactsAdmin";
import PromoAdmin from "./PromoAdmin";
import AppointmentsAdmin from "./AppointmentsAdmin";
import RequestsKanban from "../workspace/RequestsKanban";
import { registerAdminSW } from "../../lib/push";
import { enableAdminPWA } from "../../lib/pwa";
import InstallPrompt from "../../components/admin/InstallPrompt";
import ServicePreview from "./ServicePreview";
import WorkspaceLayout from "../workspace/WorkspaceLayout";
import WorkspaceDashboard from "../workspace/WorkspaceDashboard";
import Notes from "../workspace/Notes";
import Writings from "../workspace/Writings";
import Todos from "../workspace/Todos";
import Documents from "../workspace/Documents";
import Quotes from "../workspace/Quotes";
import InboxUnified from "../workspace/InboxUnified";
import WorkspaceSettings from "../workspace/WorkspaceSettings";
import Nova from "../workspace/Nova";
import PostEditor from "../shared/PostEditor";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

export const useList = (col) =>
  useQuery({ queryKey: ["admin", col], queryFn: () => api.get(`/admin/c/${col}`).then((r) => r.data) });

function Panel({ user, logout }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const redirected = useRef(false);
  const { data: settings } = useQuery({ queryKey: ["admin", "settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const { data: ai } = useQuery({ queryKey: ["admin", "ai-status"], queryFn: () => api.get("/admin/ai/status").then((r) => r.data) });
  // Load the workspace prefs so we can honour `start_on_workspace` on the
  // very first navigation after login (only if the user lands on /admin root).
  const { data: wsPrefs } = useQuery({ queryKey: ["workspace", "settings"], queryFn: () => api.get("/admin/workspace/settings").then((r) => r.data) });
  useEffect(() => {
    if (redirected.current || !wsPrefs) return;
    // Only redirect on the bare `/admin` or `/admin/` entrance.
    const path = loc.pathname.replace(/\/+$/, "");
    if (path === "/admin" && wsPrefs.start_on_workspace) {
      redirected.current = true;
      nav("/admin/workspace", { replace: true });
    } else {
      redirected.current = true;
    }
  }, [wsPrefs, loc.pathname, nav]);
  if (!settings) return <Spinner />;
  const langs = [...(settings.language?.active || ["tr"]), ...(settings.language?.extra || []).map((l) => l.code)];
  const saveSection = async (section, data) => {
    const r = await api.put(`/admin/settings/${section}`, data);
    qc.setQueryData(["admin", "settings"], r.data);
    qc.invalidateQueries({ queryKey: ["site"] });
    qc.invalidateQueries({ queryKey: ["admin", "ai-status"] });
    return r.data;
  };
  return (
    <AdminCtx.Provider value={{ user, settings, saveSection, langs: [...new Set(langs)], aiAvailable: !!ai?.available, logout }}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="menu" element={<MenuAdmin />} />
          <Route path="sayfalar" element={<PagesAdmin />} />
          <Route path="haberler" element={<NewsAdmin />} />
          <Route path="videolar" element={<VideosAdmin />} />
          <Route path="galeri" element={<GalleryAdmin />} />
          <Route path="calismalarim" element={<CalismalarimAdmin />} />
          <Route path="profil" element={<ProfileAdmin />} />
          <Route path="rehber" element={<ContactsAdmin />} />
          <Route path="reklam" element={<PromoAdmin />} />
          <Route path="istatistik" element={<AnalyticsAdmin />} />
          <Route path="post-editor" element={<PostEditor />} />
          <Route path="ayarlar" element={<Settings />} />
          {/* Legacy redirects to the moved workspace routes */}
          <Route path="takvim" element={<Navigate to="/admin/workspace/takvim" replace />} />
          <Route path="mesajlar" element={<Navigate to="/admin/workspace/mesajlar" replace />} />
          <Route path="talepler" element={<Navigate to="/admin/workspace/talepler" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
        <Route path="calismalarim/onizleme/:slug" element={<ServicePreview />} />
        <Route path="onizleme" element={<ServicePreview />} />
        {/* ---- My Workspace (private personal area) ---- */}
        <Route path="workspace" element={<WorkspaceLayout />}>
          <Route index element={<WorkspaceDashboard />} />
          <Route path="gelen-kutusu" element={<InboxUnified />} />
          <Route path="notlar" element={<Notes />} />
          <Route path="yazilar" element={<Writings />} />
          <Route path="gorevler" element={<Todos />} />
          <Route path="belgeler" element={<Documents />} />
          <Route path="alintilar" element={<Quotes />} />
          <Route path="takvim" element={<CalendarAdmin />} />
          <Route path="mesajlar" element={<MessagesAdmin />} />
          <Route path="talepler" element={<RequestsKanban />} />
          <Route path="nova" element={<Nova />} />
          <Route path="post-editor" element={<PostEditor />} />
          <Route path="ayarlar" element={<WorkspaceSettings />} />
        </Route>
      </Routes>
    </AdminCtx.Provider>
  );
}

const Spinner = () => <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-stone-400" /></div>;

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const qc = useQueryClient();
  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* already logged out */ }
    setAccessToken(null);
    localStorage.removeItem("maint_bypass");
    qc.removeQueries({ queryKey: ["admin"] });
    setUser(false);
  }, [qc]);
  useEffect(() => { if (user) localStorage.setItem("maint_bypass", "1"); }, [user]);
  useEffect(() => {
    document.title = "WorkSpace & Admin Panel";
    // Inject PWA manifest only when admin is loaded, so public site stays unchanged.
    if (typeof document !== "undefined" && !document.querySelector('link[rel="manifest"]')) {
      const l = document.createElement("link");
      l.rel = "manifest";
      l.href = "/admin.webmanifest";
      document.head.appendChild(l);
      const tc = document.createElement("meta");
      tc.name = "theme-color";
      tc.content = "#8a6a2d";
      tc.setAttribute("data-admin", "1");
      document.head.appendChild(tc);
      const icon = document.createElement("link");
      icon.rel = "apple-touch-icon";
      icon.href = "/pwa-icon-192.png";
      document.head.appendChild(icon);
    }
    // Register the admin service worker (scope: /admin/) so push works even
    // before the user opens the Push settings card.
    registerAdminSW();
    // Dynamically inject the admin manifest + iOS meta tags. Must run on both
    // the login screen AND after login so Android Chrome can decide the page
    // is installable the moment /admin is reached.
    enableAdminPWA();
    setLogoutHandler(() => setUser(false));
    refreshSession().then((d) => setUser(d.user)).catch(() => setUser(false));
  }, []);
  const onLogin = (d) => { setAccessToken(d.access_token); setUser(d.user); };
  return (
    <div className="font-body min-h-screen bg-stone-50 text-stone-900" data-testid="admin-root">
      <meta name="robots" content="noindex,nofollow" />
      {user === null ? <Spinner /> : user ? <Panel user={user} logout={logout} /> : <Login onLogin={onLogin} />}
      <InstallPrompt />
    </div>
  );
}
